import { FormEvent, useEffect, useMemo, useState } from "react";
import { ApiError } from "../../api/client";
import {
  fetchSuggestedEffectiveFrom,
  saveScheduleSettings,
  useScheduleSettingsImpact,
  useScheduleSettingsLoader,
  type SaveScheduleSettingsInput,
} from "../../api/schedulingSettings";
import { previewScheduleSlots } from "../../utils/scheduleSlotPreview";
import "../lesson-reports/LessonReportModal.css";
import "./ScheduleSettingsModal.css";

type Props = {
  open: boolean;
  /** Kullanıcınin calisma subesi (manager / tenant_admin branch_id). */
  homeBranchId: string | null;
  homeBranchName?: string | null;
  todayIso: string;
  onClose: () => void;
  onSaved: () => void;
};

function defaultForm(todayIso: string): SaveScheduleSettingsInput {
  return {
    dayStartTime: "09:00",
    dailyLessonCount: 4,
    effectiveFrom: todayIso,
    lessonDurationMinutes: 100,
    breakDurationMinutes: 20,
    breakEveryNLessons: 1,
    lunchStartTime: "12:40",
    lunchDurationMinutes: 50,
  };
}

export function ScheduleSettingsModal({
  open,
  homeBranchId,
  homeBranchName,
  todayIso,
  onClose,
  onSaved,
}: Props) {
  const resolvedBranchId = homeBranchId ?? undefined;

  const { initial, loading, error: loadError } = useScheduleSettingsLoader(
    resolvedBranchId,
    open,
  );

  const [form, setForm] = useState<SaveScheduleSettingsInput>(() => defaultForm(todayIso));
  const [dirty, setDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [suggestHint, setSuggestHint] = useState<string | null>(null);
  const [suggestLoading, setSuggestLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSubmitError(null);
    setSuggestHint(null);
    setDirty(false);
  }, [open, homeBranchId]);

  useEffect(() => {
    if (initial) {
      setForm(initial);
      setDirty(false);
    } else if (open && !loading && !loadError) {
      setForm(defaultForm(todayIso));
    }
  }, [initial, open, loading, loadError, todayIso]);

  const impact = useScheduleSettingsImpact(form.effectiveFrom, resolvedBranchId, open && !loading);

  const preview = useMemo(() => {
    try {
      return previewScheduleSlots({
        dayStartTime: form.dayStartTime,
        lessonDurationMinutes: form.lessonDurationMinutes,
        breakDurationMinutes: form.breakDurationMinutes,
        breakEveryNLessons: form.breakEveryNLessons,
        lunchStartTime: form.lunchStartTime,
        lunchDurationMinutes: form.lunchDurationMinutes,
        dailyLessonCount: form.dailyLessonCount,
      });
    } catch {
      return null;
    }
  }, [form]);

  const previewInvalid =
    preview != null && preview.actualLessonCount < preview.requestedLessonCount;

  const patch = (partial: Partial<SaveScheduleSettingsInput>) => {
    setForm((prev) => ({ ...prev, ...partial }));
    setDirty(true);
    setSubmitError(null);
  };

  const handleSuggestAfterLastAppointment = async () => {
    if (!resolvedBranchId) return;
    setSuggestLoading(true);
    setSuggestHint(null);
    try {
      const res = await fetchSuggestedEffectiveFrom(resolvedBranchId);
      patch({ effectiveFrom: res.suggested_effective_from });
      setSuggestHint(res.hint);
    } catch (err) {
      setSuggestHint(err instanceof ApiError ? err.message : "Öneri Alınamadı.");
    } finally {
      setSuggestLoading(false);
    }
  };

  const handleReset = () => {
    if (initial) setForm(initial);
    else setForm(defaultForm(todayIso));
    setDirty(false);
    setSubmitError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (previewInvalid) {
      setSubmitError(
        `Gun sonu limitini astiniz; bu surelerle en fazla ${preview?.actualLessonCount ?? 0} ders planlanabilir.`,
      );
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await saveScheduleSettings({
        ...form,
        branchId: resolvedBranchId,
      });
      onSaved();
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setSubmitError(err.message);
      } else {
        setSubmitError("Ayarlar Kaydedilemedi.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="report-modal-backdrop"
      role="presentation"
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) onClose();
      }}
    >
      <div
        className="report-modal schedule-settings-modal"
        role="dialog"
        aria-labelledby="schedule-settings-title"
        aria-modal="true"
        onClick={(ev) => ev.stopPropagation()}
      >
        <header className="report-modal__header">
          <div>
            <h3 id="schedule-settings-title">Rezervasyon Ayarları</h3>
            <p className="report-modal__sub">
              Kurumunuzun Günlük Slot Şablonu
              {homeBranchName ? (
                <>
                  {" "}
                  · <strong>{homeBranchName}</strong>
                </>
              ) : null}
              {" "}
              · Değişiklikler <strong>Kaydet</strong> İle Aktif Olur (Geçerlilik Tarihinden İtibaren)
            </p>
          </div>
          <button type="button" className="report-modal__close" onClick={onClose} aria-label="Kapat">
            ×
          </button>
        </header>

        {loadError && <p className="schedule-settings-modal__error">{loadError}</p>}
        {submitError && <p className="schedule-settings-modal__error">{submitError}</p>}
        {!homeBranchId && (
          <p className="schedule-settings-modal__error">
            Takvim ayari icin hesabiniza bir sube atanmali. Kurum yoneticisi: Kullanıcılar
            ekranindan veya sube ayarlarindan baglayin.
          </p>
        )}

        <form onSubmit={handleSubmit}>
          <div className="schedule-settings-modal__body">
            <div className="schedule-settings-form">
              <div className="schedule-settings-form__row">
                <label>
                  Gün Başlangıç Saati
                  <input
                    type="time"
                    required
                    value={form.dayStartTime}
                    onChange={(e) => patch({ dayStartTime: e.target.value })}
                    disabled={loading}
                  />
                </label>
                <label>
                  Günlük Ders Sayısı
                  <input
                    type="number"
                    min={1}
                    max={24}
                    required
                    value={form.dailyLessonCount}
                    onChange={(e) => patch({ dailyLessonCount: Number(e.target.value) })}
                    disabled={loading}
                  />
                </label>
              </div>

              <div className="schedule-settings-form__row">
                <label>
                  Ders Süresi (Dk)
                  <input
                    type="number"
                    min={15}
                    max={600}
                    required
                    value={form.lessonDurationMinutes}
                    onChange={(e) => patch({ lessonDurationMinutes: Number(e.target.value) })}
                    disabled={loading}
                  />
                </label>
                <label>
                  Dinlenme Zamanı (Dk)
                  <input
                    type="number"
                    min={0}
                    max={120}
                    required
                    value={form.breakDurationMinutes}
                    onChange={(e) => patch({ breakDurationMinutes: Number(e.target.value) })}
                    disabled={loading}
                  />
                </label>
              </div>

              <div className="schedule-settings-form__row">
                <label>
                  Mola Sıklığı
                  <select
                    required
                    value={form.breakEveryNLessons}
                    onChange={(e) => patch({ breakEveryNLessons: Number(e.target.value) })}
                    disabled={loading}
                  >
                    <option value={1}>Her Ders Arası</option>
                    <option value={2}>2 Ders Sonunda</option>
                    <option value={3}>3 Ders Sonunda</option>
                    <option value={4}>4 Ders Sonunda</option>
                  </select>
                </label>
                <p className="schedule-settings-form__hint">
                  {form.breakEveryNLessons <= 1
                    ? `Her ders arasına ${form.breakDurationMinutes} dk eklenir.`
                    : `${form.breakEveryNLessons} ders peş peşe; ardından ${
                        form.breakEveryNLessons * form.breakDurationMinutes
                      } dk mola (${form.breakEveryNLessons}×${form.breakDurationMinutes}).`}
                </p>
              </div>

              <div className="schedule-settings-form__row">
                <label>
                  Öğle Arası Başlangıç
                  <input
                    type="time"
                    required
                    value={form.lunchStartTime}
                    onChange={(e) => patch({ lunchStartTime: e.target.value })}
                    disabled={loading}
                  />
                </label>
                <label>
                  Öğle Arası Süresi (Dk)
                  <input
                    type="number"
                    min={0}
                    max={180}
                    required
                    value={form.lunchDurationMinutes}
                    onChange={(e) => patch({ lunchDurationMinutes: Number(e.target.value) })}
                    disabled={loading}
                  />
                </label>
              </div>

              <div className="schedule-settings-effective">
                <label>
                  Yeni şablon gecerlilik tarihi
                  <input
                    type="date"
                    required
                    value={form.effectiveFrom}
                    onChange={(e) => {
                      patch({ effectiveFrom: e.target.value });
                      setSuggestHint(null);
                    }}
                    disabled={loading}
                  />
                </label>
                <button
                  type="button"
                  className="kp-btn schedule-settings-effective__shortcut"
                  disabled={loading || suggestLoading || !resolvedBranchId}
                  onClick={() => void handleSuggestAfterLastAppointment()}
                >
                  {suggestLoading ? "Hesaplanıyor…" : "Son Randevudan Sonraki Gün"}
                </button>
                {suggestHint && (
                  <p className="schedule-settings-effective__hint">{suggestHint}</p>
                )}
              </div>

              <label>
                Hesaplanan Gün Sonu (Salt Okunur)
                <input type="text" readOnly value={preview?.computedDayEnd ?? "—"} tabIndex={-1} />
              </label>

              {impact.data && (
                <div
                  className={`schedule-settings-impact${
                    impact.data.warning
                      ? " schedule-settings-impact--warn"
                      : " schedule-settings-impact--ok"
                  }`}
                >
                  {impact.loading ? "Kontrol ediliyor…" : impact.data.message}
                </div>
              )}
              {impact.error && (
                <div className="schedule-settings-impact schedule-settings-impact--warn">
                  {impact.error}
                </div>
              )}
            </div>

            <aside className="schedule-settings-preview" aria-live="polite">
              <h4>Slot Önizleme</h4>
              {preview ? (
                <>
                  <p className="schedule-settings-preview__meta">
                    <strong>{preview.actualLessonCount}</strong> / {preview.requestedLessonCount}{" "}
                    ders · Bitis <strong>{preview.computedDayEnd}</strong>
                  </p>
                  {preview.midnightCapped && (
                    <p className="schedule-settings-preview__warn">
                      24:00 Sınırına Ulaşıldı; Daha Fazla Ders Bu Sürelerle Sığmaz.
                    </p>
                  )}
                  {previewInvalid && (
                    <p className="schedule-settings-preview__warn">
                      Istek {preview.requestedLessonCount} ders; en fazla{" "}
                      {preview.actualLessonCount} planlanabilir.
                    </p>
                  )}
                  <ol className="schedule-settings-preview__slots">
                    {preview.timeline.map((item, i) => {
                      if (item.kind === "lesson") {
                        return (
                          <li key={`lesson-${item.index}`} className="schedule-settings-preview__slots-item--lesson">
                            <span className="idx">{item.index}.</span>
                            <span>
                              {item.start} – {item.end}
                            </span>
                          </li>
                        );
                      }
                      if (item.kind === "break") {
                        return (
                          <li
                            key={`break-${i}`}
                            className="schedule-settings-preview__slots-item--break"
                          >
                            <span className="schedule-settings-preview__label">Dinlenme Zamanı</span>
                            <span>
                              {item.start} – {item.end} ({item.durationMinutes} dk)
                            </span>
                          </li>
                        );
                      }
                      if (item.kind === "lunch") {
                        return (
                          <li
                            key={`lunch-${i}`}
                            className="schedule-settings-preview__slots-item--lunch"
                          >
                            <span className="schedule-settings-preview__label">Öğle Arası</span>
                            <span>
                              {item.start} – {item.end}
                            </span>
                          </li>
                        );
                      }
                      return (
                        <li
                          key={`free-${i}`}
                          className="schedule-settings-preview__slots-item--free"
                        >
                          <span className="schedule-settings-preview__label">Boş Zaman</span>
                          <span>
                            {item.start} – {item.end}
                          </span>
                        </li>
                      );
                    })}
                  </ol>
                </>
              ) : (
                <p className="schedule-settings-preview__meta">Önizleme Hesaplanamadı.</p>
              )}
            </aside>
          </div>

          <footer className="schedule-settings-modal__footer">
            <button type="button" className="kp-btn" onClick={onClose} disabled={submitting}>
              Vazgeç
            </button>
            <button
              type="button"
              className="kp-btn"
              onClick={handleReset}
              disabled={submitting || loading || !dirty}
            >
              Sıfırla
            </button>
            <button
              type="submit"
              className="kp-btn kp-btn--primary"
              disabled={submitting || loading || previewInvalid || !resolvedBranchId}
            >
              {submitting ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
